<template>
  <!--
    F3/M3 (balão ③) — card de "Solicitação de Registro de Atividade" da fila unificada.
    Componente à parte de propósito: `DsAttendanceCard.vue` continua servindo folha e
    Registro de Atividade sem uma terceira variante dentro do mesmo arquivo.

    Sem push nesta fase (Q9): a cobrança É este card. Por isso ele precisa se explicar
    sozinho — qual perna falta, por qual canal o médico deve responder, até quando, e o
    que faz a cobrança sumir sem ninguém mexer nela.

    Vencida pinta o card inteiro de vermelho (`border-negative bg-red-50`, o mesmo realce da
    divergência) — mas vencer NÃO bloqueia nada: é prioridade visual, não trava.
  -->
  <!--
    O `ds-attendance-lista-card` de fora é o testid ANTIGO da fila: os e2e contam os cards por
    ele, e sem isto as linhas de cobrança sumiriam dessas contagens. Mesma disciplina que a
    §8.2 aplicou ao `ds-attendance-novo-registro-button`.
  -->
  <div data-testid="ds-attendance-lista-card">
    <div
      class="border rounded-lg p-4 py-2 mb-4"
      :class="isOverdue ? 'border-negative bg-red-50' : 'border-alert bg-orange-50'"
      data-testid="ds-attendance-request-card"
    >
      <div class="flex flex-col gap-2 py-2">
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-[#52526F] text-xs font-bold leading-[14px] tracking-[0.06px]"
            >#{{ attendanceId }}</span
          >
          <h4 class="text-textPrimary text-md font-bold">Solicitação de Registro de Atividade</h4>
          <!--
          Chips em `span` cru (e não `LabelBadge`): o texto do estado e do vencimento é a
          informação principal do card e precisa estar no texto do card, não num atributo.
        -->
          <span
            class="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-neutral50 whitespace-nowrap"
            data-testid="ds-attendance-request-status"
          >
            Aguardando envio
          </span>
          <span
            v-if="isOverdue"
            class="inline-flex items-center rounded-full border border-negative bg-red-50 px-2 py-0.5 text-[10px] font-bold text-negative whitespace-nowrap"
            data-testid="ds-attendance-request-overdue"
          >
            Vencida
            <!-- A explicação não pode viver só num `title`: leitor de tela nem sempre o anuncia. -->
            <span class="sr-only">
              — o prazo da política passou. A solicitação continua aberta: vencer não bloqueia
              aprovação, edição nem pagamento.
            </span>
          </span>
          <span
            v-else-if="dueLabel"
            class="inline-flex items-center rounded-full border border-alert bg-[#FDF1DF] px-2 py-0.5 text-[10px] font-bold text-alert whitespace-nowrap"
            data-testid="ds-attendance-request-due"
          >
            {{ dueLabel }}
          </span>
        </div>

        <div class="text-sm text-textPrimary flex flex-col gap-0.5">
          <span v-if="doctorLabel">
            <b>{{ doctorLabel }}</b>
          </span>
          <span>
            Plantão <b>{{ formattedDate }}</b> · {{ unit || 'Unidade não informada' }} ·
            {{ schedule || 'Escala não informada' }}
            <template v-if="shifts"> · {{ shifts }}</template>
          </span>
          <span data-testid="ds-attendance-request-leg">
            Perna cobrada: <b>{{ requestedLegLabel }}</b>
          </span>
          <span data-testid="ds-attendance-request-channel">
            Canal esperado: <b>{{ requestChannelLabel }}</b>
          </span>
          <span v-if="formattedDeadline" data-testid="ds-attendance-request-deadline">
            Prazo da política: <b>até {{ formattedDeadline }}</b>
          </span>
          <span v-if="formattedCreatedAt" data-testid="ds-attendance-request-opened-at">
            Aberta automaticamente em <b>{{ formattedCreatedAt }}</b>
          </span>
        </div>

        <!-- Texto literal do balão ③ do M3: quem lê o card precisa saber que não há o que
           desfazer à mão quando a perna for coberta por outro caminho. -->
        <p class="text-xs text-neutral50">
          Cancelada sozinha se chegar folha aprovada, check tardio do app, App Externo ou envio
          manual que cubra a perna.
        </p>

        <div class="flex flex-wrap items-center gap-2">
          <div class="ml-auto flex flex-wrap items-center gap-2">
            <OutlinedButton
              data-testid="ds-attendance-request-send-record"
              text="Enviar registro"
              width="150px"
              height="32px"
              @click="$emit('sendRecord', props.attendanceId)"
            />
            <OutlinedButton
              data-testid="ds-attendance-request-details"
              text="Ver detalhes"
              width="130px"
              height="32px"
              @click="$emit('details', props.attendanceId)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { PropType } from 'vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  ACTIVITY_REQUEST_CHANNEL_LABELS,
  ActivityRequestChannel,
  REQUESTED_LEG_LABELS
} from '@/constants/ds-attendance.constants'
import { DEFAULT_UTC_OFFSET, formatDateTimeWithOffset } from '@/utils/date'
import { toDDMMYYYY } from '../utils/activity-time'

const props = defineProps({
  attendanceId: {
    type: [Number, String],
    required: true
  },
  /** `yyyy-MM-dd` do plantão cobrado (o `referenceDate` do container). */
  date: { type: String, default: '' },
  unit: { type: String, default: '' },
  schedule: { type: String, default: '' },
  shifts: { type: String, default: '' },
  doctorName: { type: String as () => string | null, default: null },
  doctorCrm: { type: String as () => string | null, default: null },
  /** Vocabulário de `CoveredLegs`; valor desconhecido aparece cru em vez de sumir. */
  requestedLeg: { type: String as () => string | null, default: null },
  requestChannel: { type: String as () => string | null, default: null },
  /**
   * Prazo da cobrança. A F3 não criou campo de vencimento próprio: é o
   * `expectedSubmissionAt` do container (§6). `null` = backend anterior à F3 ou
   * solicitação sem prazo — o chip some, o card continua de pé.
   */
  expectedSubmissionAt: { type: String as () => string | null, default: null },
  /** Quando o job abriu a cobrança. Ausente em backend antigo: a linha some. */
  createdAt: { type: String as () => string | null, default: null },
  /**
   * Offset do plantão (`-03:00`), para ler `createdAt` — que é instante UTC, não parede.
   * A listagem ainda não serve o campo; sem ele o fallback é Brasília, o mesmo default de
   * `@/utils/date` e do `DsAttendanceConciliationModal`.
   */
  timezone: { type: String as () => string | null, default: null },
  /**
   * §6.5 item 3 — vencimento decidido pelo SERVIDOR (`isOverdue` do `by-company`). `null` =
   * backend sem o campo: cai no cálculo local abaixo. `default: null` evita o cast de
   * Boolean ausente para `false`.
   */
  isOverdue: { type: Boolean as PropType<boolean | null>, default: null }
})

defineEmits<{
  (e: 'details', attendanceId: number | string): void
  (e: 'sendRecord', attendanceId: number | string): void
}>()

const formattedDate = computed(() => toDDMMYYYY(props.date) || props.date)

/** Offset do plantão; Brasília quando a listagem ainda não o serve. */
const stationOffset = computed(() => props.timezone ?? DEFAULT_UTC_OFFSET)

/*
 * R2-I4 — o prazo da solicitação chega da API como INSTANTE certo (`13:00Z` para um prazo de
 * 10:00 em Brasília: o backend grava a parede e roda em `America/Sao_Paulo`). Lê-lo
 * literalmente, como o `toWallClockLabel` fazia, mostrava 13:00 — 3 h à frente. É lido no
 * fuso do plantão, o mesmo que o card da fila usa para o declarado (regra BF7).
 */
const formattedDeadline = computed(() =>
  props.expectedSubmissionAt
    ? formatDateTimeWithOffset(props.expectedSubmissionAt, stationOffset.value)
    : ''
)

/*
 * `createdAt` (N1): `created_at` é `@CreateDateColumn` do `BaseModel`, um instante REAL em
 * UTC. Lido como parede, uma cobrança aberta às 18:00 em Brasília era anunciada como 21:00.
 * Aqui vale o offset do plantão, como `DsAttendanceConciliationModal` e
 * `StationActivityRecordsSection` já fazem.
 */
const formattedCreatedAt = computed(() =>
  props.createdAt ? formatDateTimeWithOffset(props.createdAt, stationOffset.value) : ''
)

const doctorLabel = computed(() => {
  if (!props.doctorName) return ''
  return props.doctorCrm ? `${props.doctorName} (CRM ${props.doctorCrm})` : props.doctorName
})

/** Catálogo local primeiro; código fora dele aparece cru — chip vazio esconderia a cobrança. */
const requestedLegLabel = computed(
  () =>
    (props.requestedLeg ? REQUESTED_LEG_LABELS[props.requestedLeg] : null) ??
    props.requestedLeg ??
    'Perna não informada'
)

const requestChannelLabel = computed(
  () =>
    ACTIVITY_REQUEST_CHANNEL_LABELS[props.requestChannel as ActivityRequestChannel] ??
    props.requestChannel ??
    'Canal não informado'
)

/**
 * Relógio REATIVO. `Date.now()` dentro de um `computed` não cria dependência nenhuma: um card
 * montado ficava congelado em "Vence em 2 h" e nunca virava "Vencida" enquanto a fila
 * estivesse aberta — e a fila fica aberta o turno inteiro.
 *
 * Um minuto é a menor granularidade que o card mostra (horas e dias), então é o passo certo.
 */
const REFRESH_INTERVAL_MS = 60 * 1000
const now = ref(Date.now())
let refreshTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  refreshTimer = setInterval(() => {
    now.value = Date.now()
  }, REFRESH_INTERVAL_MS)
})

onBeforeUnmount(() => {
  if (refreshTimer !== null) clearInterval(refreshTimer)
  refreshTimer = null
})

/** Milissegundos até o prazo (instante real); `null` quando não há prazo legível. */
const millisecondsToDeadline = computed<number | null>(() => {
  if (!props.expectedSubmissionAt) return null
  const deadline = new Date(props.expectedSubmissionAt).getTime()
  return Number.isNaN(deadline) ? null : deadline - now.value
})

const isOverdue = computed(() => {
  if (props.isOverdue !== null) return props.isOverdue
  return millisecondsToDeadline.value !== null && millisecondsToDeadline.value <= 0
})

/**
 * Até 48 h a conta é em horas: "Vence em 1 dia" para algo que vence em 90 minutos esconde
 * a urgência de uma cobrança cujo prazo padrão do App Minha Escala é de 2 h (Q9).
 */
const dueLabel = computed(() => {
  const remaining = millisecondsToDeadline.value
  if (remaining === null || remaining <= 0) return ''
  const hours = Math.ceil(remaining / (60 * 60 * 1000))
  if (hours <= 48) return `Vence em ${hours} h`
  const days = Math.ceil(hours / 24)
  return `Vence em ${days} ${days === 1 ? 'dia' : 'dias'}`
})
</script>
